import React, { useEffect, useRef, useState } from 'react';
import { MONO, SANS } from '../../constants/fonts';
import { formatMessageTime, MAX_BODY } from '../../utils/messages';

const styles = {
  thread: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: 420,
    overflowY: 'auto',
    padding: '4px 2px',
    marginBottom: 16,
  },
  row: {
    display: 'flex',
  },
  bubble: {
    maxWidth: '80%',
    padding: '10px 14px',
    borderRadius: 14,
    fontSize: '0.87rem',
    lineHeight: 1.55,
    // Keeps the sender's own line breaks without letting a long unbroken
    // string (a URL, say) blow out the bubble width.
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  mine: {
    background: '#111',
    color: '#fff',
    borderBottomRightRadius: 4,
  },
  theirs: {
    background: '#f0f0ec',
    color: '#222',
    borderBottomLeftRadius: 4,
  },
  time: {
    fontFamily: MONO,
    fontSize: '0.68rem',
    color: '#999',
    marginTop: 5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: 74,
    fontFamily: SANS,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
  },
  counter: {
    fontFamily: MONO,
    fontSize: '0.7rem',
    color: '#aaa',
  },
  send: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    background: '#111',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  disabled: {
    opacity: 0.5,
    cursor: 'default',
  },
  empty: {
    color: '#999',
    fontSize: '0.85rem',
    margin: '0 0 16px',
  },
  error: {
    color: '#c0392b',
    fontSize: '0.85rem',
    marginTop: 10,
  },
};

// Shared by the admin panel and the client card. `side` is which end of the
// conversation this viewer is on, so their own messages sit on the right.
export default function Conversation({ messages, side, onSend, placeholder, emptyText, disabled = false }) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const threadRef = useRef(null);

  // Pin to the newest message whenever the thread grows.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const canSend = body.trim().length > 0 && !sending && !disabled;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setError('');
    setSending(true);
    try {
      await onSend(body);
      setBody('');
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {messages.length === 0 ? (
        <p style={styles.empty}>{emptyText}</p>
      ) : (
        <div style={styles.thread} ref={threadRef}>
          {messages.map((message) => {
            const isMine = message.from === side;
            return (
              <div
                key={message.id}
                style={{ ...styles.row, justifyContent: isMine ? 'flex-end' : 'flex-start' }}
              >
                <div>
                  <div style={{ ...styles.bubble, ...(isMine ? styles.mine : styles.theirs) }}>
                    {message.body}
                  </div>
                  <div style={{ ...styles.time, textAlign: isMine ? 'right' : 'left' }}>
                    {formatMessageTime(message.createdAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <form style={styles.form} onSubmit={submit}>
        <textarea
          style={styles.textarea}
          placeholder={placeholder}
          value={body}
          maxLength={MAX_BODY}
          disabled={disabled}
          onChange={(e) => setBody(e.target.value)}
        />
        <div style={styles.actions}>
          <span style={styles.counter}>{body.length}/{MAX_BODY}</span>
          <button type="submit" style={{ ...styles.send, ...(canSend ? {} : styles.disabled) }} disabled={!canSend}>
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>

      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}
